import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { backendErrorNotification } from '../../../../../../components/Notification';
import HttpStatus from '../../../../../../enums/httpStatus';
import { permissionsReq } from '../../../../../../services/requests';
import { Permission } from '../../../../../../types';

import * as s from './styles';

type PermissionGroup = {
  name: string;
  permissions: Permission[];
};

type RenderPermissionsProps = {
  selectedPermissions: Permission[];
  onChange: (permissions: Permission[]) => void;
};

type GroupToggleProps = {
  checked: boolean;
  indeterminate: boolean;
  label: string;
  onChange: () => void;
};

const normalizeText = (value: string) => value
  .toLocaleLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const GroupToggle = ({ checked, indeterminate, label, onChange }: GroupToggleProps) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <s.SectionCheckbox
      ref={checkboxRef}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      title={label}
    />
  );
};

export const RenderPermissions = ({
  selectedPermissions,
  onChange,
}: RenderPermissionsProps) => {
  const { t, i18n } = useTranslation();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [waitingGetPermissionsReq, setWaitingGetPermissionsReq] = useState(true);

  const getPermissionsReq = async () => {
    const source = permissionsReq.axios.CancelToken.source();
    setWaitingGetPermissionsReq(true);

    try {
      const response = await permissionsReq.index(source.token);

      if (response.status === HttpStatus.OK) {
        setPermissions(response.data.data);
      } else {
        throw response;
      }

      setWaitingGetPermissionsReq(false);
    } catch (err) {
      if (!permissionsReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      setWaitingGetPermissionsReq(false);
    }
  };

  const language = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const selected = selectedPermissions ?? [];

  const permissionGroups = useMemo<PermissionGroup[]>(() => {
    const groupedPermissions = new Map<string, Permission[]>();

    permissions.forEach((permission) => {
      const groupName = permission.name.split('-')[0];
      const groupPermissions = groupedPermissions.get(groupName) ?? [];

      groupPermissions.push(permission);
      groupedPermissions.set(groupName, groupPermissions);
    });

    const translatedPermission = (permission: Permission) => String(
      t(`bk.permission.${permission.name}`, { defaultValue: permission.name }),
    );
    const translatedGroup = (group: PermissionGroup) => String(
      t(group.name, { defaultValue: group.name }),
    );
    const compareTranslated = (first: string, second: string) => (
      first.localeCompare(second, language, { sensitivity: 'base', numeric: true })
    );

    return Array.from(groupedPermissions.entries())
      .map(([name, groupPermissions]) => ({
        name,
        permissions: groupPermissions.sort((first, second) => (
          compareTranslated(translatedPermission(first), translatedPermission(second))
          || first.name.localeCompare(second.name)
        )),
      }))
      .sort((first, second) => (
        compareTranslated(translatedGroup(first), translatedGroup(second))
        || first.name.localeCompare(second.name)
      ));
  }, [language, permissions, t]);

  const filteredPermissionGroups = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm.trim());

    return permissionGroups
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => {
          if (!normalizedSearch) return true;

          const translatedPermission = String(
            t(`bk.permission.${permission.name}`, { defaultValue: permission.name }),
          );

          return normalizeText(translatedPermission).includes(normalizedSearch);
        }),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [permissionGroups, searchTerm, t]);

  const handleCheckBox = (permission: Permission) => {
    const selectedPermCopy = [...selected];

    if (selectedPermCopy.some((item) => item.name === permission.name)) {
      onChange(selectedPermCopy.filter((item) => item.name !== permission.name));
    } else {
      selectedPermCopy.push(permission);
      onChange(selectedPermCopy);
    }
  };

  const handleToggleGroup = (groupPermissions: Permission[]) => {
    const allSelected = groupPermissions.length > 0
      && groupPermissions.every((permission) => (
        selected.some((item) => item.name === permission.name)
      ));

    if (allSelected) {
      onChange(selected.filter((item) => (
        !groupPermissions.some((permission) => permission.name === item.name)
      )));
      return;
    }

    const permissionsToAdd = groupPermissions.filter((permission) => (
      !selected.some((item) => item.name === permission.name)
    ));

    onChange([...selected, ...permissionsToAdd]);
  };

  useEffect(() => {
    getPermissionsReq();
  }, []);

  return waitingGetPermissionsReq ? (
    <div> {t('loading') as string}...</div>
  ) : (
    <>
      <s.SearchContainer>
        <AiOutlineSearch aria-hidden="true" />
        <input
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={t('searchPermissions') as string}
          aria-label={t('searchPermissions') as string}
        />
      </s.SearchContainer>

      {filteredPermissionGroups.map((group) => {
        const isGroupFullySelected = group.permissions.length > 0
          && group.permissions.every((permission) => (
            selected.some((item) => item.name === permission.name)
          ));
        const isGroupPartiallySelected = !isGroupFullySelected
          && group.permissions.some((permission) => (
            selected.some((item) => item.name === permission.name)
          ));
        const groupLabel = t(group.name, { defaultValue: group.name }) as string;
        const toggleActionLabel = t(
          isGroupFullySelected ? 'deselectAll' : 'selectAll',
        ) as string;

        return (
          <div key={group.name} style={{ marginTop: '1rem' }}>
            <s.GroupHeader>
              <GroupToggle
                checked={isGroupFullySelected}
                indeterminate={isGroupPartiallySelected}
                label={`${toggleActionLabel}: ${groupLabel}`}
                onChange={() => handleToggleGroup(group.permissions)}
              />
              <s.TitleGroup>{groupLabel}</s.TitleGroup>
            </s.GroupHeader>

            {group.permissions.map((permission) => (
              <s.ItemGroup key={permission.id}>
                <>
                  <input
                    type="checkbox"
                    onChange={() => handleCheckBox(permission)}
                    checked={selected.some((item) => item.name === permission.name)}
                  />
                  {t(`bk.permission.${permission.name}`, { defaultValue: permission.name })}
                </>
              </s.ItemGroup>
            ))}
          </div>
        );
      })}

      {searchTerm.trim() && filteredPermissionGroups.length === 0 && (
        <s.EmptyState>{t('noPermissionsFound') as string}</s.EmptyState>
      )}
    </>
  );
};

export default RenderPermissions;
